import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import useTacticValidation from './TacticValidation.hook';
import TacticValidationDialogConfig from './TacticValidationDialog.config';
import { mayaCepEditRoute } from 'basics/constants/routes.constants';
import { useBulkUpdateTacticMutation } from 'basics/graphql/mutations/bulkUpdateTactic';
import { useUpdateTacticMutation } from 'basics/graphql/mutations/updateTactic';
import { tacticToBulkUpdateInput } from 'basics/transformers/Tactic.transformer';
import { CepStateType } from 'basics/types/maya.types';
import { succesToast, warningToast } from 'basics/utils/toast';
import cepState from 'states/cep.states';

const useTacticValidationDialog = (
  setOpen: Dispatch<SetStateAction<boolean>>,
  cepId: string | undefined,
) => {
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const { notValidGroups } = useTacticValidation();
  const [updateTactic] = useUpdateTacticMutation();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [bulkUpdateTactic] = useBulkUpdateTacticMutation();

  const handleCloseDialog = () => {
    setOpen(false);
  };

  const handleSubmitDialog = () => {
    window.location.href = `${mayaCepEditRoute}?id=${cepStateValue.cep?._id || cepId}&step=2`;
  };

  const dialogConfig = TacticValidationDialogConfig(
    handleCloseDialog,
    handleSubmitDialog,
  );

  const getTacticsByGroupId = (groupId: string) => {
    return cepStateValue?.tactics?.filter((tactic) => tactic?.parentTactic?._id === groupId);
  };

  const removeGroup = async (groupId: string) => {
    const currentCepId = cepStateValue.cep?._id || cepId;

    if (!currentCepId) {
      return;
    }

    setLoading(true);
    const tacticsWithPreviousField = cepStateValue.tactics.filter((tactic) => tactic?.previousTactic?._id === groupId);
    const tacticsToBulkUpdate = tacticsWithPreviousField.map(
      (tactic) => tacticToBulkUpdateInput({ ...tactic, previousTactic: null }, currentCepId),
    );

    if (tacticsToBulkUpdate.length) {
      const {
        data: updateTactics,
        error: bulkError,
      } = await bulkUpdateTactic(currentCepId, tacticsToBulkUpdate, true);

      if (updateTactics && !bulkError) {
        cepState.tactics = cepState.tactics.map((tactic) => (
          tactic?.previousTactic?._id === groupId ? { ...tactic, previousTactic: null } : tactic));
      }
    }

    const relatedTactic = cepStateValue.tactics.find((tactic) => tactic?.parentTactic?._id === groupId);
    const tacticId = relatedTactic?._id;
    const isOnlyOneTacticForUpdate = notValidGroups.length === 1;

    if (!tacticId || !currentCepId) {
      warningToast(t('errors_standard'));
      return;
    }

    const { data: tacticData, error } = await updateTactic(tacticId, { parentTactic: null }, currentCepId, true);
    if (tacticData && !error) {
      cepState.tactics = cepState.tactics.map((tactic) => (
        tactic._id === tacticId ? { ...tactic, parentTactic: null } : tactic));

      succesToast(t('maya_tactic_updated', { title: tacticData.title }));

      if (isOnlyOneTacticForUpdate) {
        handleCloseDialog();
        succesToast(t('maya_tactic_not_valid_group_removed', { title: tacticData.title }));
      }
    } else {
      warningToast(t('errors_standard'));
    }

    setLoading(false);
  };

  return {
    notValidGroups,
    getTacticsByGroupId,
    dialogConfig,
    handleCloseDialog,
    removeGroup,
    loading,
  };
};

export default useTacticValidationDialog;
